// app/components/users/UserModal.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const UserModal = ({ isOpen, onClose, onSave, user, roles }) => {
    const [formData, setFormData] = useState({
        username: '',
        name: '',
        mobile_number: '',
        role: 'agent',
        role_id: null,
        is_active: true,
        preferred_language: 'en'
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            // Handle both old single role format and new multiple roles format
            let userRole = user.role;
            let userRoleId = user.role_id;
            
            // If user has multiple roles, use the first one for editing (since backend replaces all roles)
            if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
                const firstRole = user.roles[0];
                userRole = firstRole.code || firstRole.role_code;
                userRoleId = firstRole.id;
            }
            
            setFormData({
                username: user.username,
                name: user.name,
                mobile_number: user.mobile_number,
                role: userRole,
                role_id: userRoleId,
                is_active: user.is_active,
                preferred_language: user.preferred_language || 'en'
            });
        } else {
            // For new users, set default role to the first available role or AGENT
            const defaultRole = roles.find(r => r.role_code === 'AGENT') || roles[0];
            setFormData({
                username: '',
                name: '',
                mobile_number: '',
                role: defaultRole ? defaultRole.role_code.toLowerCase() : 'agent',
                role_id: defaultRole ? defaultRole.id : null,
                is_active: true,
                preferred_language: 'en'
            });
        }
        setErrors({});
    }, [user, roles]);

    const validate = () => {
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        }

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.mobile_number.trim()) {
            newErrors.mobile_number = 'Mobile number is required';
        } else if (!/^\+?[1-9][0-9]{7,14}$/.test(formData.mobile_number)) {
            newErrors.mobile_number = 'Invalid mobile number format';
        }

        if (!formData.role_id) {
            newErrors.role = 'Role is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            // Get the selected role to send the correct role_code
            const selectedRole = roles.find(r => r.id === formData.role_id);
            
            // Prepare data for backend
            const submitData = {
                username: formData.username,
                name: formData.name,
                mobile_number: formData.mobile_number,
                role: selectedRole?.role_code || 'AGENT', // Send role_code (e.g., 'AGENT', 'ADMIN')
                role_id: formData.role_id,
                is_active: formData.is_active,
                preferred_language: formData.preferred_language
            };
            
            await onSave(submitData);
            onClose();
        } catch (error) {
            console.error('Error saving user:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = (roleCode) => {
        const selectedRole = roles.find(r => r.role_code === roleCode);
        if (selectedRole) {
            setFormData({
                ...formData,
                role: roleCode.toLowerCase(),
                role_id: selectedRole.id
            });
        }
    };

    // Get the current role code for the dropdown value
    const getCurrentRoleCode = () => {
        if (formData.role_id) {
            // Find role by role_id
            const role = roles.find(r => r.id === formData.role_id);
            return role?.role_code || '';
        }
        // Fallback to first role if no role_id is set
        return roles.length > 0 ? roles[0].role_code : '';
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={user ? 'Edit User' : 'Add New User'}
            size="md"
        >
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-normal text-gray-600 mb-1">
                            Username
                        </label>
                        <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.username ? 'border-red-500' : 'border-gray-300'
                                }`}
                            disabled={!!user}
                        />
                        {errors.username && (
                            <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-normal text-gray-600 mb-1">
                            Name
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                        />
                        {errors.name && (
                            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-normal text-gray-600 mb-1">
                            Mobile Number
                        </label>
                        <input
                            type="tel"
                            value={formData.mobile_number}
                            onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.mobile_number ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="+919876543210"
                        />
                        {errors.mobile_number && (
                            <p className="mt-1 text-sm text-red-600">{errors.mobile_number}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-normal text-gray-600 mb-1">
                            Role
                        </label>
                        {user && user.roles && user.roles.length > 1 && (
                            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-xs text-yellow-800">
                                    ⚠️ This user has {user.roles.length} roles. Editing will replace all roles with the selected one.
                                </p>
                            </div>
                        )}
                        <select
                            value={getCurrentRoleCode()}
                            onChange={(e) => handleRoleChange(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.role ? 'border-red-500' : 'border-gray-300'
                                }`}
                        >
                            {roles.map(role => (
                                <option key={role.id} value={role.role_code}>
                                    {role.role_name}
                                </option>
                            ))}
                        </select>
                        {errors.role && (
                            <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-normal text-gray-600 mb-1">
                            Language
                        </label>
                        <select
                            value={formData.preferred_language}
                            onChange={(e) => setFormData({ ...formData, preferred_language: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="en">English</option>
                            <option value="hi">Hindi</option>
                            <option value="te">Telugu</option>
                        </select>
                    </div>

                    <div className="flex items-center">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">Active</span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-center gap-3 pt-5 pb-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : (user ? 'Update' : 'Create')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default UserModal;   